import { formatEur, type ExpensesScheduleDto, type SchedulePeriod } from "@gladis/shared";
import { BurnDownChart } from "@gladis/ui";
import { formatDayAndMonth, formatMonthOnly, useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

/**
 * The days the axis names: every day of a week, a week apart in a month, each month's first in a year — and in
 * the compact year, each QUARTER's first, because twelve month names do not fit half a Dashboard row.
 */
function ticksFor(
  window: { from: string; to: string },
  period: SchedulePeriod,
  compact: boolean,
): string[] {
  const ticks: string[] = [];
  const step = period === "week" ? 1 : 7;
  const yearTick = compact ? /-(01|04|07|10)-01$/ : /-01$/;
  for (let day = window.from; day <= window.to;) {
    if (period !== "year" || yearTick.test(day)) ticks.push(day);
    const next = new Date(`${day}T00:00:00Z`);
    next.setUTCDate(next.getUTCDate() + (period === "year" ? 1 : step));
    day = next.toISOString().slice(0, 10);
  }
  return ticks;
}

/**
 * The "still to go out" step chart, wired to the schedule read (feature 024).
 *
 * ONE COMPONENT FOR BOTH PLACES IT APPEARS — the Expenses page section and the Dashboard card — so the two
 * cannot come to draw the same reply differently: the same ticks, the same tooltip naming each step's
 * payments (FR-016), the same empty sentences. `compact` is the Dashboard's size and nothing else.
 */
export function ScheduleChart({
  data,
  period,
  compact = false,
}: {
  data: ExpensesScheduleDto;
  period: SchedulePeriod;
  compact?: boolean;
}): ReactNode {
  const t = useT();
  const stepOn = new Map(data.steps.map((step) => [step.date, step]));
  const nothingDated = data.steps.length === 0 && data.undated.length > 0;

  return (
    <BurnDownChart
      steps={data.steps.map((step) => ({
        at: step.date,
        amountCents: Math.round(step.totalInEuros * 100),
      }))}
      window={data.window}
      today={data.asOf}
      pastLabel={t("exp.schedule.goneOut")}
      remainingLabel={t("exp.schedule.stillToGoOut")}
      todayLabel={t("exp.schedule.today")}
      ariaLabel={t("exp.schedule.chartLabel")}
      formatValue={(value) => formatEur(value / 100)}
      formatTick={(day) =>
        period === "year"
          ? formatMonthOnly(Number(day.slice(0, 4)), Number(day.slice(5, 7)))
          : formatDayAndMonth(day)
      }
      ticks={ticksFor(data.window, period, compact)}
      renderTooltip={(day) => {
        const step = stepOn.get(day);
        if (step === undefined) return null;
        return (
          <>
            <p className="mb-1 font-medium">{formatDayAndMonth(day)}</p>
            {step.payments.map((payment) => (
              <p key={payment.rowKey} className="flex justify-between gap-6">
                <span className="text-muted">{payment.description}</span>
                <span className="tabular-nums">{formatEur(payment.amountInEuros)}</span>
              </p>
            ))}
          </>
        );
      }}
      emptyMessage={
        nothingDated ? t("exp.schedule.noDatesYet") : t("exp.schedule.nothingScheduled")
      }
      compact={compact}
    />
  );
}
